import { DownloadCloudIcon, Loader2Icon, ShieldBanIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, buttonVariants } from '@folio/components/ui';
import { cn } from '@folio/lib';
import { documentsControllerGetDocuments } from 'korint-sdk';
import { Text } from '@folio/components/ui/text';

interface DocumentDownloadButtonProps {
  document: {
    id?: string;
    metadata: Record<string, unknown>;
    signedUrl?: string;
    name?: string;
  };
  showButtonText: boolean;
  children?: React.ReactNode;
  onClick?: () => void;
}

export function DocumentDownloadButton({
  document,
  showButtonText,
  children,
  onClick,
}: DocumentDownloadButtonProps) {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const scanResult = document.metadata.scanresult as string | undefined;
  const downloadFilename =
    document.name || (document.metadata.name as string | undefined) || 'document';

  if (scanResult === 'CLEAN' || scanResult === 'UNSUPPORTED') {
    if (onClick) {
      const handleClick = async () => {
        setIsDownloading(true);
        try {
          await onClick();
        } finally {
          setIsDownloading(false);
        }
      };

      return (
        <Button
          type="button"
          variant="ghost"
          size="xs"
          aria-label={
            !showButtonText && !children ? t('documents.downloadButton.download') : undefined
          }
          onClick={handleClick}
          disabled={isDownloading}
          className={cn(
            'gap-2',
            children
              ? 'h-auto whitespace-normal p-0 font-normal hover:bg-transparent'
              : 'min-h-9 min-w-9 md:min-h-8 md:min-w-8',
          )}
        >
          {showButtonText && t('documents.downloadButton.download')}
          {isDownloading ? (
            <Loader2Icon className="size-4 animate-spin" />
          ) : (
            <DownloadCloudIcon className="size-4" />
          )}
          {children}
        </Button>
      );
    }

    const handleDownload = async (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      if (!document.signedUrl || isDownloading) return;

      setIsDownloading(true);

      try {
        const url = document.signedUrl;
        const isSameOrigin = url.startsWith('/') || url.startsWith(window.location.origin);

        // For same-origin URLs, use fetch/blob
        if (isSameOrigin) {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = globalThis.document.createElement('a');
          link.href = blobUrl;
          link.download = downloadFilename;
          globalThis.document.body.appendChild(link);
          link.click();
          globalThis.document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        } else if (document.id) {
          const result = await documentsControllerGetDocuments({
            ids: [document.id],
            forceDownload: true,
          } as Parameters<typeof documentsControllerGetDocuments>[0]);

          if (result.documents?.[0]?.signedUrl) {
            const downloadUrl = result.documents[0].signedUrl;
            const link = globalThis.document.createElement('a');
            link.href = downloadUrl;
            link.download = downloadFilename;
            globalThis.document.body.appendChild(link);
            link.click();
            globalThis.document.body.removeChild(link);
          } else {
            throw new Error('Failed to get download URL');
          }
        } else {
          window.open(url, '_blank');
        }
      } catch (error) {
        if (document.signedUrl) {
          window.open(document.signedUrl, '_blank');
        }
      } finally {
        setIsDownloading(false);
      }
    };

    return (
      <a
        aria-label={
          !showButtonText && !children ? t('documents.downloadButton.download') : undefined
        }
        href={document.signedUrl}
        download={downloadFilename}
        onClick={handleDownload}
        className={cn(
          'flex items-center gap-2 hover:text-foreground',
          !children && buttonVariants({ variant: 'ghost', size: 'xs' }),
          !children && 'min-h-9 min-w-9 md:min-h-8 md:min-w-8 justify-center',
          isDownloading && 'pointer-events-none opacity-50',
        )}
      >
        {showButtonText && t('documents.downloadButton.download')}
        {isDownloading ? (
          <Loader2Icon className="size-4 animate-spin" />
        ) : (
          <DownloadCloudIcon className="size-4" />
        )}
        {children}
      </a>
    );
  }

  if (scanResult === 'INFECTED') {
    return (
      <div
        className={cn(
          'flex items-center gap-2 text-red-600',
          !children && buttonVariants({ variant: 'ghost', size: 'xs' }),
          !children && 'min-h-9 min-w-9 md:min-h-8 md:min-w-8 justify-center',
        )}
      >
        {showButtonText && t('documents.downloadButton.infected')}
        <ShieldBanIcon className="size-4" />
        {children}
        {children && (
          <Text size="sm" weight="medium" className="block">
            {t('documents.downloadButton.malwareWarning')}
          </Text>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2',
        !children && buttonVariants({ variant: 'ghost', size: 'xs' }),
        !children && 'min-h-9 min-w-9 md:min-h-8 md:min-w-8 justify-center',
      )}
    >
      {showButtonText && t('documents.downloadButton.scanning')}
      <Loader2Icon className="size-4 ml-2 animate-spin" />
      {children}
    </div>
  );
}
